import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { enableTotpLoginTemplate, requestEnableTotpLoginTemplate } from 'apollo/templates/user/mutation.graphql';
import { User } from 'models/user';
import QRCode from 'react-qr-code';
import { currentUserVar } from 'apollo/reactive-variables/user';

type EnableGoogleAuthDialogProps = {
    open: boolean;
    onClose: () => void;
};
const EnableGoogleAuthDialog: FC<EnableGoogleAuthDialogProps> = ({ open, onClose }: EnableGoogleAuthDialogProps) => {
    const currentUser = currentUserVar();
    const { t: translate } = useTranslation(['user-settings', 'common']);
    const [isLoading, setIsLoading] = useState(true);
    const { showBoundary } = useDenthubErrorBoundary();
    const [code, setCode] = useState('');
    const [qrCodeValue, setQrCodeValue] = useState<string>();
    const [isInvalidCode, setIsInvalidCode] = useState(false);

    const [requestEnableTotpLogin] = useMutation(requestEnableTotpLoginTemplate, {
        onCompleted: ({ requestEnableTotpLogin }) => {
            setIsLoading(false);
            if (requestEnableTotpLogin) {
                setQrCodeValue(requestEnableTotpLogin);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [enableTotpLogin] = useMutation(enableTotpLoginTemplate, {
        onCompleted: ({ enableTotpLogin }) => {
            if (enableTotpLogin) {
                currentUser.isTotpEnabled = true;
                currentUserVar(currentUser);
                setIsLoading(false);
                onClose();
            }
        },
        onError(error) {
            if (error.message === 'Failed to toggle TOTP Login') {
                setIsInvalidCode(true);
                setIsLoading(false);
                return;
            }
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (open) {
            requestEnableTotpLogin();
        }
    }, [open]);

    const handleEnableTotpLogin = () => {
        setIsLoading(true);
        enableTotpLogin({ variables: { code: code } });
    };

    const handleCodeChange = (value: string) => {
        setIsInvalidCode(false);
        setCode(value);
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={open}>
            <DialogTitle>{translate('user-settings:ENABLE_GOOGLE_AUTH')}</DialogTitle>
            <DialogContent>
                <Box mb={1} sx={{ width: '100%' }}>
                    <Box mb={2}>{translate('user-settings:ENABLE_GOOGLE_AUTH_INSTRUCTIONS')}</Box>
                    <Box mb={2}>
                        {qrCodeValue && (
                            <Box display="flex" justifyContent="center">
                                <QRCode size={150} value={qrCodeValue} />
                            </Box>
                        )}
                    </Box>
                    <Box>
                        <TextField
                            fullWidth
                            value={code}
                            onChange={(event) => handleCodeChange(event.target.value)}
                            label={translate('user-settings:GOOGLE_AUTH_CODE')}
                            error={isInvalidCode}
                            helperText={isInvalidCode ? translate('user-settings:INVALID_GOOGLE_AUTH_CODE') : undefined}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{translate('common:CANCEL')}</Button>
                <LoadingButton
                    variant="contained"
                    onClick={() => handleEnableTotpLogin()}
                    loading={isLoading}
                    disabled={!code || code.length < 6 || isInvalidCode}
                >
                    {translate('user-settings:ENABLE_GOOGLE_AUTH')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default EnableGoogleAuthDialog;
